import { Timestamp } from 'firebase/firestore';
import { getEventsByOrganization } from './eventService';
import { getVolunteersByOrganization } from './userService';
import { getRegistrationsByOrganization } from './registrationService';

/**
 * Get event metrics for an organization
 * @param {string} organizationId - Organization ID
 * @param {Object} dateRange - Optional date range {start, end}
 * @returns {Promise<Object>} Event metrics
 */
export const getEventMetrics = async (organizationId, dateRange = null) => {
  try {
    const events = await getEventsByOrganization(organizationId);
    const now = Timestamp.now();

    // Apply date filter if provided
    let filteredEvents = events;
    if (dateRange?.start || dateRange?.end) {
      filteredEvents = events.filter((event) => {
        const eventTime = event.date?.toMillis?.() || 0;
        if (dateRange.start && eventTime < dateRange.start.toMillis()) return false;
        if (dateRange.end && eventTime > dateRange.end.toMillis()) return false;
        return true;
      });
    }

    const totalEvents = filteredEvents.length;
    const upcomingEvents = filteredEvents.filter(
      (e) => (e.date?.toMillis?.() || 0) >= now.toMillis()
    ).length;
    const completedEvents = filteredEvents.filter((e) => e.status === 'completed').length;
    const cancelledEvents = filteredEvents.filter((e) => e.status === 'cancelled').length;

    // Calculate average fulfillment rate
    const eventsWithVolunteers = filteredEvents.filter((e) => e.volunteersNeeded > 0);
    let totalFulfillmentRate = 0;
    eventsWithVolunteers.forEach((event) => {
      const rate = ((event.volunteersRegistered || 0) / event.volunteersNeeded) * 100;
      totalFulfillmentRate += rate;
    });
    const averageFulfillmentRate = eventsWithVolunteers.length > 0
      ? totalFulfillmentRate / eventsWithVolunteers.length
      : 0;

    // Total volunteer spots
    const totalVolunteerSpots = filteredEvents.reduce(
      (sum, event) => sum + (event.volunteersNeeded || 0),
      0
    );
    const totalVolunteersRegistered = filteredEvents.reduce(
      (sum, event) => sum + (event.volunteersRegistered || 0),
      0
    );

    return {
      totalEvents,
      upcomingEvents,
      completedEvents,
      cancelledEvents,
      averageFulfillmentRate: Math.round(averageFulfillmentRate),
      totalVolunteerSpots,
      totalVolunteersRegistered,
    };
  } catch (error) {
    console.error('Error getting event metrics:', error);
    throw new Error('Failed to get event metrics: ' + error.message);
  }
};

/**
 * Get top events by registration count
 * @param {string} organizationId - Organization ID
 * @param {number} limit - Number of top events to return
 * @returns {Promise<Array>} Top events
 */
export const getTopEvents = async (organizationId, limit = 5) => {
  try {
    const events = await getEventsByOrganization(organizationId);

    // Sort by volunteersRegistered (descending)
    const sortedEvents = events
      .filter((e) => (e.volunteersRegistered || 0) > 0)
      .sort((a, b) => (b.volunteersRegistered || 0) - (a.volunteersRegistered || 0))
      .slice(0, limit);

    return sortedEvents.map((event) => ({
      title: event.title,
      volunteersRegistered: event.volunteersRegistered || 0,
      volunteersNeeded: event.volunteersNeeded || 0,
      fulfillmentRate: event.volunteersNeeded > 0
        ? Math.round(((event.volunteersRegistered || 0) / event.volunteersNeeded) * 100)
        : 0,
      date: event.date,
    }));
  } catch (error) {
    console.error('Error getting top events:', error);
    throw new Error('Failed to get top events: ' + error.message);
  }
};

/**
 * Get volunteer metrics for an organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Volunteer metrics
 */
export const getVolunteerMetrics = async (organizationId) => {
  try {
    const volunteers = await getVolunteersByOrganization(organizationId);

    const totalVolunteers = volunteers.length;
    const activeVolunteers = volunteers.filter((v) => v.status === 'active').length;
    const pendingVolunteers = volunteers.filter((v) => v.status === 'pending').length;
    const rejectedVolunteers = volunteers.filter((v) => v.status === 'rejected').length;

    // Calculate total hours served
    const totalHoursServed = volunteers.reduce(
      (sum, volunteer) => sum + (volunteer.totalHours || 0),
      0
    );

    // Average hours per volunteer
    const averageHoursPerVolunteer = activeVolunteers > 0
      ? totalHoursServed / activeVolunteers
      : 0;

    return {
      totalVolunteers,
      activeVolunteers,
      pendingVolunteers,
      rejectedVolunteers,
      totalHoursServed: Math.round(totalHoursServed),
      averageHoursPerVolunteer: Math.round(averageHoursPerVolunteer * 10) / 10,
    };
  } catch (error) {
    console.error('Error getting volunteer metrics:', error);
    throw new Error('Failed to get volunteer metrics: ' + error.message);
  }
};

/**
 * Get top volunteers by hours served
 * @param {string} organizationId - Organization ID
 * @param {number} limit - Number of top volunteers to return
 * @returns {Promise<Array>} Top volunteers
 */
export const getTopVolunteers = async (organizationId, limit = 10) => {
  try {
    const volunteers = await getVolunteersByOrganization(organizationId);

    // Filter active volunteers with hours and sort
    const sortedVolunteers = volunteers
      .filter((v) => v.status === 'active' && (v.totalHours || 0) > 0)
      .sort((a, b) => (b.totalHours || 0) - (a.totalHours || 0))
      .slice(0, limit);

    return sortedVolunteers.map((volunteer) => ({
      name: volunteer.displayName || volunteer.email,
      email: volunteer.email,
      totalHours: volunteer.totalHours || 0,
      skills: volunteer.skills || [],
    }));
  } catch (error) {
    console.error('Error getting top volunteers:', error);
    throw new Error('Failed to get top volunteers: ' + error.message);
  }
};

/**
 * Get volunteer growth trend over time
 * @param {string} organizationId - Organization ID
 * @param {number} months - Number of months to include (default 6)
 * @returns {Promise<Array>} Monthly volunteer counts
 */
export const getVolunteerGrowthTrend = async (organizationId, months = 6) => {
  try {
    const volunteers = await getVolunteersByOrganization(organizationId);
    const now = new Date();

    // Generate array of past N months
    const monthlyData = [];
    for (let i = months - 1; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);

      // Count volunteers created in this month
      const volunteersInMonth = volunteers.filter((v) => {
        const createdDate = v.createdAt?.toDate?.() || new Date(0);
        return createdDate >= monthDate && createdDate <= monthEnd;
      }).length;

      // Count active volunteers at end of month
      const activeAtMonth = volunteers.filter((v) => {
        const createdDate = v.createdAt?.toDate?.() || new Date(0);
        return createdDate <= monthEnd && v.status === 'active';
      }).length;

      monthlyData.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        newVolunteers: volunteersInMonth,
        activeVolunteers: activeAtMonth,
      });
    }

    return monthlyData;
  } catch (error) {
    console.error('Error getting volunteer growth trend:', error);
    throw new Error('Failed to get volunteer growth trend: ' + error.message);
  }
};

/**
 * Get event creation trend over time
 * @param {string} organizationId - Organization ID
 * @param {number} months - Number of months to include (default 6)
 * @returns {Promise<Array>} Monthly event counts
 */
export const getEventTrend = async (organizationId, months = 6) => {
  try {
    const events = await getEventsByOrganization(organizationId);
    const now = new Date();

    // Generate array of past N months
    const monthlyData = [];
    for (let i = months - 1; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);

      // Count events in this month
      const eventsInMonth = events.filter((e) => {
        const eventDate = e.date?.toDate?.() || new Date(0);
        return eventDate >= monthDate && eventDate <= monthEnd;
      });

      const totalEvents = eventsInMonth.length;
      const completedEvents = eventsInMonth.filter((e) => e.status === 'completed').length;

      // Calculate average fulfillment for month
      const eventsWithVolunteers = eventsInMonth.filter((e) => e.volunteersNeeded > 0);
      let totalFulfillment = 0;
      eventsWithVolunteers.forEach((event) => {
        totalFulfillment += ((event.volunteersRegistered || 0) / event.volunteersNeeded) * 100;
      });
      const avgFulfillment = eventsWithVolunteers.length > 0
        ? totalFulfillment / eventsWithVolunteers.length
        : 0;

      monthlyData.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        totalEvents,
        completedEvents,
        averageFulfillment: Math.round(avgFulfillment),
      });
    }

    return monthlyData;
  } catch (error) {
    console.error('Error getting event trend:', error);
    throw new Error('Failed to get event trend: ' + error.message);
  }
};

/**
 * Get registration/attendance analytics
 * @param {string} organizationId - Organization ID
 * @param {Object} dateRange - Optional date range {start, end}
 * @returns {Promise<Object>} Registration metrics
 */
export const getRegistrationMetrics = async (organizationId, dateRange = null) => {
  try {
    const registrations = await getRegistrationsByOrganization(organizationId, dateRange);

    const totalRegistrations = registrations.length;
    const activeRegistrations = registrations.filter((r) => r.status === 'registered').length;
    const attendedRegistrations = registrations.filter((r) => r.status === 'attended').length;
    const cancelledRegistrations = registrations.filter((r) => r.status === 'cancelled').length;

    // Calculate attendance rate (attended / (attended + no-show))
    const completedEvents = registrations.filter(
      (r) => r.status === 'attended' || r.status === 'no-show'
    );
    const attendanceRate = completedEvents.length > 0
      ? (attendedRegistrations / completedEvents.length) * 100
      : 0;

    return {
      totalRegistrations,
      activeRegistrations,
      attendedRegistrations,
      cancelledRegistrations,
      attendanceRate: Math.round(attendanceRate),
    };
  } catch (error) {
    console.error('Error getting registration metrics:', error);
    throw new Error('Failed to get registration metrics: ' + error.message);
  }
};

/**
 * Get comprehensive dashboard analytics
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} All analytics data
 */
export const getDashboardAnalytics = async (organizationId) => {
  try {
    const [eventMetrics, volunteerMetrics, registrationMetrics] = await Promise.all([
      getEventMetrics(organizationId),
      getVolunteerMetrics(organizationId),
      getRegistrationMetrics(organizationId),
    ]);

    return {
      events: eventMetrics,
      volunteers: volunteerMetrics,
      registrations: registrationMetrics,
    };
  } catch (error) {
    console.error('Error getting dashboard analytics:', error);
    throw new Error('Failed to get dashboard analytics: ' + error.message);
  }
};
